import React from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Header, KeyboardAwareView, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { useAdminMenu } from '@hooks'
import { Theme } from '@theme'
import { AdminTab, AdminTabs } from './AdminTabs'

interface AdminPageProps {
  title: string
  onBack: () => void
  /** Which tab of the admin navigation is highlighted. */
  active: AdminTab
  isPending?: boolean
  children: React.ReactNode
}

/** Shared shell of the super-admin screens: header + tabs + centered, width-capped content. */
export const AdminPage: React.FC<AdminPageProps> = ({
  title,
  onBack,
  active,
  isPending,
  children,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { isSuperAdmin, isLoading } = useAdminMenu()
  // Only a super-admin sees the panel; anyone else gets a plain refusal (the API refuses too).
  const denied = !isLoading && !isSuperAdmin
  return (
    <Screen isPending={isPending}>
      <Header title={title} iconLeft="arrowBack" onPressLeft={onBack} />
      <KeyboardAwareView isFullScreen androidOnly offsetTopInset>
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          // iOS: fields of the admin forms stay above the keyboard.
          automaticallyAdjustKeyboardInsets
        >
          <View style={styles.content}>
            {isLoading ? (
              <ActivityIndicator color={theme.colors.primary} />
            ) : denied ? (
              <Text type="subtitleBold" center testID="admin-forbidden">
                {t('admin.forbidden')}
              </Text>
            ) : (
              <>
                <AdminTabs active={active} />
                {children}
              </>
            )}
          </View>
        </ScrollView>
      </KeyboardAwareView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    scroll: { padding: theme.spacing.lg, alignItems: 'center' },
    content: { width: '100%', maxWidth: 960, gap: theme.spacing.md },
  })
